import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logout, sessionQuery } from '@/lib/api/queries'
import { currentAccount } from '@/lib/session'
import { Icon } from '@/components/icons/icon'

function initials(name: string | null | undefined, email: string) {
  const source = name?.trim() || email
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

/**
 * Who is signed in, at the foot of the sidebar. Collapses to the avatar when the
 * sidebar does; the classes key off the sidebar's own data attributes, so it
 * needs no sidebar context and renders anywhere.
 */
export function AccountMenu() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const session = useQuery(sessionQuery)

  const signOut = useMutation({
    mutationFn: logout,
    // Clear every cached query, not just the session: the next person to sign in
    // on this browser must never see the previous one's data.
    onSuccess: async () => {
      queryClient.clear()
      await navigate('/login', { replace: true })
    },
  })

  if (!session.data) return null
  const { email, full_name, avatar_url } = session.data
  // The account this session last landed in, not whichever sorts first.
  const current = currentAccount(session.data)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account menu"
        className="flex w-full min-w-0 items-center gap-3 rounded-[11px] p-2 text-left outline-hidden transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring data-popup-open:bg-sidebar-accent group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:p-0"
      >
        <Avatar className="size-9 group-data-[collapsible=icon]:size-8">
          {avatar_url && <AvatarImage src={avatar_url} alt="" />}
          <AvatarFallback className="bg-(image:--sweep) text-micro font-bold text-white">
            {initials(full_name, email)}
          </AvatarFallback>
        </Avatar>
        <span className="grid min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
          <span className="truncate text-small font-semibold">{full_name ?? email}</span>
          {full_name ? <span className="truncate text-caption text-muted-foreground">{email}</span> : null}
        </span>
        <Icon
          name="chev"
          className="size-4 shrink-0 text-faint group-data-[collapsible=icon]:hidden"
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-64 rounded-xl p-1.5 shadow-card-lift">
        {/* Base UI requires GroupLabel to sit inside a Group -- a bare label
            throws MenuGroupContext is missing and takes the route down with it.
            This is where it differs from Radix, whose label is standalone. */}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="truncate px-2.5 py-2 text-caption font-normal text-muted-foreground">
            {email}
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        {current && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="grid gap-0.5 px-2.5 py-2 font-normal">
                <span className="truncate text-small font-semibold text-foreground">{current.name}</span>
                <span className="text-caption text-muted-foreground capitalize">{current.role}</span>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
          </>
        )}
        <DropdownMenuSeparator />
        {/* onClick, not Radix's onSelect. Base UI's Menu.Item has no onSelect,
            and because the underlying element is a div, `onSelect` is a valid
            DOM prop (text selection) -- so it type-checks and silently never
            fires. Sign out did nothing at all until this was found in a browser. */}
        <DropdownMenuItem
          onClick={() => signOut.mutate()}
          disabled={signOut.isPending}
          className="gap-2.5 rounded-[9px] px-2.5 py-2 text-small"
        >
          <Icon name="logout" className="size-4" />
          {signOut.isPending ? 'Signing out…' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
